import { useRef } from 'react';
import { PreBuiltGraphs } from '@/components/Dashboard/DefaultGraphs';
import { selectGraphsReadOnly } from '@/redux/selectors/dashboardSelectors';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AnimationSpec } from 'chart.js';
import ChartLegend from '../../Reusable/ChartLegend/ChartLegend';
import PieChart, { PieChartData } from '../../Reusable/PieChart';
import { ColorMap } from '../../Reusable/chartConfig';
import { NonMatrixedChartData, buildChartColors, legendMouseLeave, legendMouseOver } from '../../Reusable/chartUtils';
import { useAppSelector } from '@/redux/hooks';

interface AverageTimesWidgetProps {
  colors: ColorMap;
  loading: boolean;
  animation: AnimationSpec<any>;
  data: (Pick<PieChartData, 'values' | 'colors'> & { title: string })[];
  noData: {
    review: boolean;
    edit: boolean;
  };
}

const generateTimeLabel = (min: number) => {
  const time = Math.round(min);
  return `${time < 1 ? '< 1' : time.toLocaleString()} min`;
};

const AverageTimesWidget = ({ colors: colorMap, loading, animation, data, noData }: AverageTimesWidgetProps) => {
  const chartColors = buildChartColors(2, [data[0].colors[0], data[1].colors[0]], colorMap);
  const theme = useTheme();
  const chartOne = useRef();
  const chartTwo = useRef();
  const readOnly = useAppSelector(selectGraphsReadOnly);
  const rowFilter = readOnly.order.filter((row) => row.includes(PreBuiltGraphs.AVERAGE_TIMES));
  const rowLength = rowFilter.length > 0 ? rowFilter[0].length : 0;
  const sizeStyling = rowLength >= 3 ? 'squished' : rowLength <= 1 ? 'alone' : 'normal';

  const ChartLabel = ({ num, noData }: { num: number; noData: boolean }) =>
    noData ? (
      <Label>
        <Typography style={{ fontStyle: 'italic' }} fontSize="1.5rem">
          No Data
        </Typography>
      </Label>
    ) : (
      <Label>
        <Typography variant="h3" fontSize="1.5rem" lineHeight="1.5">
          {generateTimeLabel(num)}
        </Typography>
        <span>Average Time</span>
      </Label>
    );

  const dataColors = (dark: string, light: string) => {
    return theme.palette.mode === 'dark' ? [dark, light] : [light, dark];
  };

  return (
    <Wrapper size={sizeStyling}>
      <PieWrapper size={sizeStyling}>
        <PieChart
          ref={chartOne}
          title={data[0].title}
          data={{
            id: PreBuiltGraphs.AVERAGE_TIMES,
            values: data[0].values,
            labels: ['', ''],
            colors: dataColors(chartColors[0].end, chartColors[0].start),
            legend: 'square'
          }}
          colors={colorMap}
          animation={animation}
          loading={loading}
          disableLegend
        />
        {!loading && <ChartLabel num={data[0].values[0]} noData={noData.edit} />}
      </PieWrapper>
      <LegendWrapper size={sizeStyling}>
        <ChartLegend
          data={
            {
              values: [data[0].values[0], data[1].values[0]],
              colors: [data[0].colors[0], data[1].colors[0]],
              labels: [data[0].title, data[1].title],
              legend: 'circle'
            } as NonMatrixedChartData
          }
          colors={chartColors}
          loading={loading}
          skeletonOrientation="vertical"
          mouseOverCallback={(i: number) => {
            legendMouseOver(i === 0 ? chartOne : chartTwo, 0);
          }}
          mouseLeaveCallback={() => {
            legendMouseLeave([chartOne, chartTwo]);
          }}
        />
      </LegendWrapper>
      <PieWrapper size={sizeStyling}>
        <PieChart
          ref={chartTwo}
          title={data[1].title}
          data={{
            id: PreBuiltGraphs.AVERAGE_TIMES,
            values: data[1].values,
            labels: ['', ''],
            colors: dataColors(chartColors[1].end, chartColors[1].start),
            legend: 'square'
          }}
          colors={colorMap}
          animation={animation}
          loading={loading}
          disableLegend
        />
        {!loading && <ChartLabel num={data[1].values[0]} noData={noData.review} />}
      </PieWrapper>
    </Wrapper>
  );
};

export default AverageTimesWidget;

type SizeProp = { size: string };
const Wrapper = styled.div<SizeProp>`
  display: flex;
  flex-direction: ${({ size }) => (size === 'squished' ? 'column' : 'row')};
  justify-content: center;
  align-items: center;
  width: 100%;
  column-gap: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
  margin: ${({ theme }) => theme.spacing(2, 0)};

  ${({ theme, size }) =>
    size === 'squished' &&
    `
    & > div {
      margin: ${theme.spacing(2)} 0;
    }
  `}

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-wrap: wrap;
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    flex-direction: row;
    flex-wrap: ${({ size }) => (size === 'squished' ? 'wrap' : 'nowrap')};
  }

  ${({ theme }) => theme.breakpoints.up('xl')} {
    & > div {
      margin: 0;
    }
`;

const PieWrapper = styled.div<SizeProp>`
  width: 175px;
  position: relative;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 150px;
    order: ${({ size }) => (size !== 'squished' ? 'unset' : 1)};
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    ${({ size }) => size !== 'squished' && `width: 175px;`}
    order: ${({ size }) => (size !== 'squished' ? 'unset' : 1)};
  }

  ${({ theme }) => theme.breakpoints.up('xl')} {
    width: 200px;
  }
`;

const LegendWrapper = styled.div<SizeProp>`
.MuiSkeleton-root {
  width: 75px;
}
margin: ${({ theme }) => theme.spacing(0, 2)};

  ${({ theme }) => theme.breakpoints.up('md')} {
    .MuiSkeleton-root {
      width: 80px;
    }
      order: ${({ size }) => (size !== 'squished' ? 'unset' : 2)};
ß  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    order: ${({ size }) => (size !== 'squished' ? 'unset' : 2)};
  }

  ${({ theme }) => theme.breakpoints.up('xl')} {
      .MuiSkeleton-root {
        width: 100px;
      }
    }
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  position: absolute;
  top: 0px;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;
